import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { CSSProperties, useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { IOptionsProps } from '../../../../@types/reactSelect/IOption'
import { useEditOnboarding } from '../../../../hooks/useEditOnboarding'
import { userService } from '../../../../services/userService'
import { fetchStateCities, fetchStatesUF } from '../../../../services/ibge'
import { sortBy } from '../../../../utils/arrays/sortBy'
import { Input } from '../../components/Input'
import { Dropdown } from '../../components/Select'
import { FooterEditOnboarding } from '../../Footer'
import './../../styles.css'
import './styles.css'
import { ReactComponent as UploadIcon } from '../../../../assets/svg/icons/upload.svg'
import { FileUploadKml } from '../../components/FileUploadKml'
import { SliderComponent } from '../../components/Slider/Index'
import Constants from '../../../../constants/Index'
import { DrawOnMap } from '../../components/DrawOnMap/DrawOnMap'
import { Chips } from '../../../Chips'
import { cpf, cnpj } from 'cpf-cnpj-validator'
import { ButtonRedirect } from '../../../Modal/Error/ButtonRedirect/Index'
import { AlertModal } from '../../../Modal/Alert/Index'
import { TitleModal } from '../../../Modal/Contents/TitleModal/Index'
import { TextModal } from '../../../Modal/Contents/TextModal/Index'
import { SimpleButtonClose } from '../../../Modal/Contents/SimpleButtonClose/Index'
import axios from '../../../../services/axios'

interface IFormFarmData {
  name: string
  initials: string
  document: string
  state_registration: string
  owner_user_id: {
    label: string
    value: string
  } | null
  address: {
    state: {
      label: string
      value: string
    } | null
    city: {
      label: string
      value: string
    } | null
  }
  map_coords: { lat: number; lng: number }[]
}

export function CreateFarm() {
  const farmSchema = yup.object().shape({
    name: yup
      .string()
      .required('Campo obrigatório não informado.'),
    initials: yup
      .string()
      .matches(/^\S+(\s+\S+)*$/, 'Esta sigla não é válida.')
      .required('Sigla é necessário.'),
    document: yup
      .string()
      .notRequired()
      .test('documentCPF', 'CPF informado inválido.', (value) => {
        const valueParsed = value?.toString().replaceAll('.', '').replaceAll('-', '')
        if (!!valueParsed && valueParsed.length < 12) return cpf.isValid(valueParsed);
        return true;
      })
      .test('documentCNPJ', 'CNPJ informado inválido.', (value) => {
        const valueParsed = value?.toString().replaceAll('.', '').replaceAll('-', '')
        if (!!valueParsed && valueParsed.length > 11 && valueParsed.length < 15) return cnpj.isValid(value || '');
        return true;
      }),
    state_registration: yup.string(),
    owner_user_id: yup
      .object({
        label: yup.string(),
        value: yup.string(),
      })
      .nullable()
      .required('Campo obrigatório não informado.'),
    address: yup.object({
      state: yup
        .object({
          label: yup.string(),
          value: yup.string().required('UF é necessário.'),
        })
        .nullable()
        .required('UF é necessário.'),
      city: yup
        .object({
          label: yup.string(),
          value: yup.string().required('Cidade é necessário.'),
        })
        .nullable()
        .required('Cidade é necessário.'),
    }),
    map_coords: yup
      .array()
      .nullable()
      .min(1, 'Mapa da fazenda é necessário.'),
  });

  const { setOptionForm, defaultValue } = useEditOnboarding()
  const [submiting, setSubmiting] = useState(false)
  const isNew = !defaultValue
  const [listUF, setListUF] = useState<IOptionsProps>([])
  const [managers, setManagers] = useState<IOptionsProps>([])
  const [listCities, setListCities] = useState<IOptionsProps>([])
  const [blockCities, setBlockCities] = useState<boolean>(true)
  const [areasList, setAreasList] = useState<any[]>()
  const [initialsOnlyRead, setInitialsOnlyRead] = useState<boolean>(isNew)
  const [maskCFPCNPJ, setMaskCFPCNPJ] = useState<string>('999.999.999-999999')
  const [inicioChuva, setInicioChuva] = useState(isNew ? 3 : defaultValue?.season?.rain_initial)
  const [inicioSeca, setInicioSeca] = useState(isNew ? 8 : defaultValue?.season?.dry_initial)
  const [msgError, setMsgError] = useState<string>('')

  const contentStyleAlert = {
    width: '335px',
    height: '237px',
    borderRadius: '20px',
    padding: '20px',
  } as CSSProperties

  const formDefaultValues = useMemo(() => {
    if (!defaultValue) {
      return {
        name: '',
        initials: '',
        document: '',
        state_registration: '',
        owner_user_id: null,
        address: {
          state: null,
          city: null,
        },
        map_coords: []
      }
    }
    
    const manager = managers?.find(m => m.value === defaultValue.owner_user_id) || null;
    
    return {
      name: defaultValue.name || '',
      initials: defaultValue.initials || '',
      document: defaultValue.document || '',
      state_registration: defaultValue.state_registration || '',
      owner_user_id: manager,
      address: {
        state: {
          label: defaultValue.address.state,
          value: defaultValue.address.state,
        },
        city: {
          label: defaultValue.address.city.name,
          value: defaultValue.address.city.code,
        },
      },
      map_coords: defaultValue.map_coords || []
    }
  }, [defaultValue, managers])

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IFormFarmData>({
    defaultValues: formDefaultValues,
    resolver: yupResolver(farmSchema),
  });

  const docInput = watch('document')
  const nameInput = watch('name')
  const stateDropdown = watch('address.state')

  useEffect(() => {
    if (!isNew && defaultValue?.id)
      axios
        .get('/areas/', { params: { farm_id: defaultValue.id } })
        .then(({ data }) => setAreasList(data));
  }, [defaultValue]);

  const deleteFarm = () => {
    if (defaultValue?.id) {
      axios
        .delete(`/farms/${defaultValue.id}`)
        .then(() => {
          setOptionForm(undefined);
        })
        .catch((err) => setMsgError(err.response.data.msg));
    }
  }

  const onSubmit = (data: IFormFarmData) => {
    setSubmiting(true);

    if (isNew) {
      var formCreateFarm = [
        {
          name: data?.name,
          initials: data?.initials,
          document:
            data?.document != ""
              ? data?.document.replace(/[^0-9a-z]/gi, "")
              : undefined,
          address: {
            city: {
              code: data?.address?.city?.value,
              name: data?.address?.city?.label,
            },
            state: data?.address?.state?.label,
          },
          properties: {
            season: {
              dry_initial: inicioSeca,
              rain_initial: inicioChuva,
            },
          },
          state_registration: data?.state_registration,
          owner_user_id: data?.owner_user_id?.value,
          map_coords: data?.map_coords,
        },
      ];
      axios
        .post("/farms", formCreateFarm)
        .then(() => {
          setOptionForm(undefined);
        })
        .catch((err) => setMsgError(err.response.data.msg))
        .finally(() => {
          setSubmiting(false);
        });
    } else {
      var formPathFarm = [
        {
          farm_id: defaultValue?.id,
          state_registration: data?.state_registration,
          name: data?.name,
          document: data?.document,
          owner_user_id: data.owner_user_id?.value,
          properties: {
            season: {
              dry_initial: inicioSeca,
              rain_initial: inicioChuva,
            },
          },
        },
      ];

      axios
        .patch("/farms", formPathFarm)
        .then(() => {
          setOptionForm(undefined);
        })
        .catch((err) => setMsgError(err.response.data.msg))
        .finally(() => {
          setSubmiting(false);
        });
    }
  };

  useEffect(() => {
    fetchStatesUF().then((data) => {
      const ufs: Array<any> = data.map((uf: any) => ({
        label: uf,
        value: uf,
      }));
      setListUF(() => ufs.sort(sortBy("label")));
    });
    
    userService
      .getUsersByOffice(Constants.PROFILE_CODE_GERENTE.toString())
      .then((data) => {
        setManagers(
          data.map((user: any) => ({
            value: user.id,
            label: user.name,
          }))
        )
      })
  }, []);

  useEffect(() => {
    if (docInput) {
      docInput.length <= 14
        ? setMaskCFPCNPJ('999.999.999-999999')
        : setMaskCFPCNPJ('99.999.999/9999-99');
    } else setValue('document', '');
  }, [docInput, setValue]);

  useEffect(() => {
    if (initialsOnlyRead) {
      if (nameInput) {
        const nameFarm = nameInput.toLocaleUpperCase().split(" ");
        if (nameFarm[0] != "") {
          if (nameFarm.length == 1) {
            setValue('initials', `${nameFarm[0][0]}${nameFarm[0][1] || ""}`);
          } else if (nameFarm[1] !== "") {
            setValue('initials', `${nameFarm[0][0]}${nameFarm[1][0]}`);
          }
        }
      } else setValue('initials', '');
    }
  }, [nameInput, initialsOnlyRead, setValue]);

  useEffect(() => {
    if (stateDropdown) {
      fetchStateCities(stateDropdown.value).then((data) => {
        const response = data
          .map((city: any) => ({ label: city.nome, value: city.id }))
          .sort();
        setListCities(() => response);
      });
      setBlockCities(false);
    }
  }, [stateDropdown]);

  useEffect(() => {
    if (listUF && managers) {
      reset(formDefaultValues)
    }
  }, [defaultValue, listUF, managers, reset, formDefaultValues]);

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError('')} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>Algo deu errado.</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError('')}>
            Voltar
          </ButtonRedirect>
        </section>
      </AlertModal>
      <main className={"body__formEditOnboarding"}>
        <div className="firstColumn">
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: 500 }}>
            <Input
              control={control}
              label={'Nome da Fazenda'}
              theme={'normal'}
              errorText={errors?.name?.message as any}
              name={'name'}
            />

            <Input
              control={control}
              label={'Sigla'}
              theme={!isNew || initialsOnlyRead ? 'read-only' : 'normal'}
              disabled={!isNew || initialsOnlyRead}
              maxLength={3}
              errorText={
                !initialsOnlyRead ? (errors?.initials as any)?.message : ''
              }
              name={'initials'}
            />

            {isNew && (
              <label
                onClick={() => setInitialsOnlyRead(!initialsOnlyRead)}
                className="initial--readOnly"
              >
                {initialsOnlyRead ? "Alterar sigla" : "Usar sigla automática"}
              </label>
            )}

            <Input
              control={control}
              label={'CPF / CNPJ'}
              theme={'normal'}
              mask={maskCFPCNPJ}
              errorText={(errors?.document as any)?.message}
              name={'document'}
            />

            <Input
              control={control}
              label={'Inscrição Estadual'}
              theme={'normal'}
              name={'state_registration'}
            />

            <div className="city__container">
              <Dropdown
                control={control}
                label={'UF'}
                closeMenuOnSelect={true}
                placeholderText={'UF'}
                options={listUF}
                className={'city__container__uf'}
                isDisabled={!isNew}
                isSearchable={isNew}
                name={'address.state'}
                errorText={
                  (errors?.address as any)?.state?.message ? ' ' : undefined
                }
              />

              <Dropdown
                control={control}
                label={'Cidade'}
                closeMenuOnSelect={true}
                placeholderText={'Cidade'}
                options={listCities}
                className={'city__container__city'}
                isDisabled={!isNew || blockCities}
                isSearchable={isNew && !blockCities}
                name={'address.city'}
                errorText={
                  (errors?.address as any)?.city?.message ? ' ' : undefined
                }
              />
            </div>

            {errors?.address ? (
              <small className="textInput--small-error animation__fadeIn">
                UF e CIDADE é necessário
              </small>
            ) : (
              ""
            )}

            <Dropdown
              label={'Responsável da Fazenda'}
              control={control}
              closeMenuOnSelect={true}
              placeholderText={'Selecione...'}
              options={managers}
              isSearchable={isNew}
              name={'owner_user_id'}
              errorText={(errors?.owner_user_id as any)?.value?.message}
            />

            <div className="season_definition">
              <label className="rhf__header--label">Definição de Estação</label>
              <div className="barSeason">
                <SliderComponent
                  listOptions={Constants.MONTHS_SHORT_NAME}
                  rain_initial={inicioChuva}
                  dry_initial={inicioSeca}
                  setRainInitial={setInicioChuva}
                  setDryInitial={setInicioSeca}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="secondColumn">
          {isNew ? (
            <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 5 }}>
              <FileUploadKml
                title="*Clique para adicionar sua fazenda."
                control={control}
                errorText={errors?.map_coords?.message as any}
                name={'map_coords'}
              >
                <UploadIcon />
                <strong>Upload Mapa KML</strong>
              </FileUploadKml>
            </form>
          ) : (
            <>
              <DrawOnMap
                staticsElements={
                  (areasList && areasList.map((e: any) => e.map_coords)) || []
                }
                style={{ width: '400px', height: '300px' }}
                mainPolygon={defaultValue?.map_coords || []}
                initialsLabel={defaultValue?.initials || ''}
                control={control}
                name={'map_coords'}
              />

              <ul>
                {areasList &&
                  areasList.map((area) => (
                    <li key={area.id}>
                      <Chips.Pressable
                        variationStyles={{ container: { width: "100%" } }}
                        theme={"link-light"}
                        label={`${area?.farm?.initials}-${area?.initials}`}
                      />
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={handleSubmit((data)=>{
          if(!submiting){
            onSubmit(data)
          }
        })}
        deleteFunction={deleteFarm}
        isNew={isNew}
        disabledSubmit={submiting}
      />
    </>
  );
}
